/**
 * Rooms list — the operator hub. The registry is tenant-scoped: in 'picker' mode
 * (dev) it renders the tenant picker when no tenant is selected; in 'fixed' mode
 * (prod) it is locked to the operator's own tenant. With a tenant it loads the
 * rooms with LIVE health (the tenant-health strip + a per-room status column), and
 * offers client-side search + status filtering, plus create/detail navigation.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import Button from '@8x8/oxygen-button';
import Input from '@8x8/oxygen-input';
import { PlusIcon, RefreshIcon, SearchIcon } from '@8x8/oxygen-icon';
import type { AdminRoomSummary, RoomStatus } from '@spaces/contracts';
import type { SessionState } from '../api';
import { listRooms } from '../api';
import { navigate } from '../router';
import { rememberTenant } from '../tenant';
import { fmtRelative } from '../ui/format';
import {
    HealthIndicator,
    Muted,
    Notice,
    PageTitle,
    SkeletonRows,
    StatusTag
} from '../ui/primitives';
import { HealthStrip } from '../ui/HealthStrip';
import { ClickRow, PrimaryCellText, SecondaryCellText, TableEl, Td, Th } from '../ui/table';
import { AdminPinButton } from './AdminPin';

const Page = styled.div`
    padding: 16px 24px 32px;
`;

const Header = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 18px;
`;

const TableCard = styled.div`
    background: ${p => p.theme.ui06};
    border: 1px solid ${p => p.theme.ui01};
    border-radius: 8px;
    overflow: hidden;
`;

const Toolbar = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    border-bottom: 1px solid ${p => p.theme.ui01};
    flex-wrap: wrap;
`;

const Segmented = styled.div`
    display: flex;
    gap: 2px;
    padding: 2px;
    background: ${p => p.theme.ui25};
    border-radius: 6px;
`;

const Seg = styled.button<{ $active: boolean }>`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    border: none;
    cursor: pointer;
    padding: 6px 12px;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 500;
    background: ${p => (p.$active ? p.theme.ui06 : 'transparent')};
    color: ${p => (p.$active ? p.theme.textColor01 : p.theme.textColor02)};
    box-shadow: ${p => (p.$active ? '0 1px 3px rgba(0,0,0,.12)' : 'none')};
`;

const SegCount = styled.span`
    font-size: 11px;
    color: ${p => p.theme.textColor05};
    padding: 1px 6px;
    background: ${p => p.theme.ui25};
    border-radius: 1000px;
`;

const NoMatch = styled.div`
    padding: 36px;
    text-align: center;
    font-size: 14px;
    color: ${p => p.theme.textColor02};
`;

type StatusFilter = 'all' | RoomStatus;
const FILTERS: StatusFilter[] = ['all', 'active', 'provisioning', 'disabled', 'archived'];
const FILTER_LABEL: Record<StatusFilter, string> = {
    all: 'All',
    active: 'Active',
    provisioning: 'Provisioning',
    disabled: 'Disabled',
    archived: 'Archived'
};

function matches(summary: AdminRoomSummary, term: string, status: StatusFilter): boolean {
    const { room } = summary;
    const okStatus = status === 'all' || room.status === status;
    const t = term.trim().toLowerCase();
    const okTerm = t.length === 0
        || room.name.toLowerCase().includes(t)
        || room.id.toLowerCase().includes(t)
        || (room.countryCode ?? '').toLowerCase().includes(t);

    return okStatus && okTerm;
}

export function RoomsList({ session }: { session: SessionState }): JSX.Element {
    // The tenant is the operator's own (their 8x8 token's cid) — no picker.
    const tenant = session.tenant ?? null;

    if (!tenant) {
        return (
            <Page>
                <Notice kind="warning">Your token carries no tenant — sign in again.</Notice>
            </Page>
        );
    }

    return <RoomsListForTenant tenant={tenant} />;
}

function RoomsListForTenant({ tenant }: { tenant: string }): JSX.Element {
    const [rooms, setRooms] = useState<AdminRoomSummary[] | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [term, setTerm] = useState('');
    const [status, setStatus] = useState<StatusFilter>('all');
    const [reloadKey, setReloadKey] = useState(0);
    const reqId = useRef(0);

    useEffect(() => {
        rememberTenant(tenant);
    }, [tenant]);

    useEffect(() => {
        const id = ++reqId.current;

        setRooms(null);
        setError(null);

        void listRooms(tenant).then(result => {
            if (id !== reqId.current) {
                return;
            }
            if (result.ok) {
                setRooms(result.data.rooms);
            } else {
                setError(result.message);
            }
        });
    }, [tenant, reloadKey]);

    const counts = useMemo(() => {
        const c: Record<StatusFilter, number> = { all: 0, active: 0, provisioning: 0, disabled: 0, archived: 0 };

        (rooms ?? []).forEach(s => {
            c.all += 1;
            c[s.room.status] += 1;
        });

        return c;
    }, [rooms]);

    const visible = useMemo(
        () => (rooms ?? []).filter(s => matches(s, term, status)),
        [rooms, term, status]
    );

    return (
        <Page>
            <Header>
                <PageTitle>Rooms</PageTitle>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <AdminPinButton />
                    <Button
                        variant="secondary"
                        iconLeft={<RefreshIcon size={15} />}
                        onClick={() => setReloadKey(k => k + 1)}
                    >
                        Refresh
                    </Button>
                    <Button
                        variant="primary"
                        iconLeft={<PlusIcon size={15} />}
                        onClick={() => navigate(`/rooms/new?tenant=${encodeURIComponent(tenant)}`)}
                    >
                        New room
                    </Button>
                </div>
            </Header>

            <HealthStrip tenant={tenant} reloadKey={reloadKey} />

            <TableCard>
                <Toolbar>
                    <div style={{ minWidth: 300, flex: '0 1 360px' }}>
                        <Input
                            value={term}
                            placeholder="Search name, id, country…"
                            iconLeft={SearchIcon}
                            fullWidth
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTerm(e.target.value)}
                        />
                    </div>
                    <Segmented>
                        {FILTERS.map(f => (
                            <Seg key={f} $active={status === f} onClick={() => setStatus(f)}>
                                {FILTER_LABEL[f]}
                                <SegCount>{counts[f]}</SegCount>
                            </Seg>
                        ))}
                    </Segmented>
                </Toolbar>

                {error ? (
                    <div style={{ padding: 16 }}>
                        <Notice kind="warning">Couldn&apos;t load rooms: {error}</Notice>
                        <div style={{ marginTop: 12 }}>
                            <Button variant="secondary" onClick={() => setReloadKey(k => k + 1)}>Retry</Button>
                        </div>
                    </div>
                ) : rooms === null ? (
                    <SkeletonRows rows={5} />
                ) : (
                    <RoomsTable rooms={visible} onClear={() => { setTerm(''); setStatus('all'); }} totalLoaded={rooms.length} />
                )}
            </TableCard>
        </Page>
    );
}

function RoomsTable({
    rooms,
    onClear,
    totalLoaded
}: {
    rooms: AdminRoomSummary[];
    onClear: () => void;
    totalLoaded: number;
}): JSX.Element {
    if (rooms.length === 0) {
        if (totalLoaded === 0) {
            return <NoMatch>No rooms in this tenant yet. Use “New room” to provision one.</NoMatch>;
        }

        return (
            <NoMatch>
                No rooms match.{' '}
                <Button variant="text" onClick={onClear}>Clear filter</Button>
            </NoMatch>
        );
    }

    return (
        <TableEl>
            <thead>
                <tr>
                    <Th>Room</Th>
                    <Th>Tenant</Th>
                    <Th>Status</Th>
                    <Th>TV health</Th>
                    <Th $align="right">Devices</Th>
                    <Th>Country</Th>
                    <Th>Updated</Th>
                </tr>
            </thead>
            <tbody>
                {rooms.map(({ room, deviceCount, health }) => (
                    <ClickRow key={room.id} onClick={() => navigate(`/rooms/${encodeURIComponent(room.id)}`)}>
                        <Td>
                            <PrimaryCellText>{room.name}</PrimaryCellText>
                            <SecondaryCellText>{room.id}</SecondaryCellText>
                        </Td>
                        <Td><Muted>{room.tenant}</Muted></Td>
                        <Td><StatusTag status={room.status} /></Td>
                        <Td><HealthIndicator health={health} /></Td>
                        <Td $align="right" style={{ fontVariantNumeric: 'tabular-nums', fontSize: 14 }}>{deviceCount}</Td>
                        <Td><Muted>{room.countryCode ?? '—'}</Muted></Td>
                        <Td><Muted>{fmtRelative(room.updatedAt)}</Muted></Td>
                    </ClickRow>
                ))}
            </tbody>
        </TableEl>
    );
}
